import { Layout } from "@/components/Layout";
import { SEO } from "@/components/SEO";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Shield, Code2, Zap, Lock, CheckCircle2 } from "lucide-react";

const HowItWorks = () => {
  return (
    <Layout>
      <SEO 
        title="How RandomGen Works - Cryptographic Algorithm Explained"
        description="Learn how RandomGen generates truly random numbers using the Web Crypto API's cryptographically secure random number generator. Technical transparency for developers and researchers."
        keywords="how RNG works, crypto.getRandomValues, cryptographic random, Web Crypto API, CSPRNG, algorithm explanation"
      />

      <div className="container mx-auto px-4 py-12 max-w-4xl">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">
            <Shield className="w-3 h-3 mr-1" />
            Cryptographically Secure
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
            How RandomGen Works
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Full transparency into our cryptographically secure random number generation algorithm
          </p>
        </div>

        {/* Quick Summary */}
        <Card className="mb-8 border-primary/20 bg-primary/5">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <Zap className="w-6 h-6 text-accent mt-1 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-lg mb-2">TL;DR: Fast, Secure, Browser-Native</h3>
                <p className="text-muted-foreground">
                  RandomGen uses <code className="bg-muted px-2 py-1 rounded text-sm">crypto.getRandomValues()</code> from the Web Crypto API - 
                  the same cryptographically secure RNG that protects your HTTPS connections, password managers, and cryptocurrency wallets.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Main Algorithm Explanation */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Code2 className="w-5 h-5 text-primary" />
              The Algorithm: crypto.getRandomValues()
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h3 className="font-semibold text-lg mb-2">What Is It?</h3>
              <p className="text-muted-foreground leading-relaxed">
                <code>crypto.getRandomValues()</code> is part of the W3C Web Cryptography API standard, implemented in all modern browsers. 
                It's a <strong>Cryptographically Secure Pseudo-Random Number Generator (CSPRNG)</strong> designed for security-critical applications.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-lg mb-2">How Does It Generate Randomness?</h3>
              <p className="text-muted-foreground leading-relaxed mb-3">
                The browser's CSPRNG draws entropy from multiple unpredictable sources:
              </p>
              <ul className="space-y-2 text-muted-foreground">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" />
                  <span><strong>Hardware randomness:</strong> CPU instructions (RDRAND/RDSEED on Intel/AMD), TPM chips, sensor noise</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" />
                  <span><strong>System entropy pools:</strong> Keyboard timings, mouse movements, disk I/O timing, network jitter</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" />
                  <span><strong>Cryptographic mixing:</strong> Fed through algorithms like HMAC-DRBG or CTR-DRBG (NIST SP 800-90A)</span>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-lg mb-2">Why Is This Better Than Math.random()?</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 font-semibold">Feature</th>
                      <th className="text-left py-2 font-semibold">Math.random()</th>
                      <th className="text-left py-2 font-semibold">crypto.getRandomValues()</th>
                    </tr>
                  </thead>
                  <tbody className="text-muted-foreground">
                    <tr className="border-b">
                      <td className="py-2">Security</td>
                      <td className="py-2">❌ Predictable</td>
                      <td className="py-2">✅ Cryptographically secure</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2">Entropy Source</td>
                      <td className="py-2">Simple seed (time-based)</td>
                      <td className="py-2">Hardware + OS entropy pool</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2">Use Case</td>
                      <td className="py-2">Animations, games</td>
                      <td className="py-2">Security, lotteries, cryptography</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2">Standard</td>
                      <td className="py-2">ES1 (1997)</td>
                      <td className="py-2">W3C Web Crypto API (2017)</td>
                    </tr>
                    <tr>
                      <td className="py-2">Speed</td>
                      <td className="py-2">Faster (but insecure)</td>
                      <td className="py-2">Very fast (still secure)</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Code Example */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Code2 className="w-5 h-5 text-accent" />
              Simplified Code Example
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground mb-4">
              Here's a simplified version of how RandomGen generates a random number in a range:
            </p>
            <pre className="bg-muted p-4 rounded-lg overflow-x-auto text-sm">
{`function generateSecureRandom(min: number, max: number): number {
  // Create array to hold random values
  const randomBuffer = new Uint32Array(1);
  
  // Fill with cryptographically secure random bytes
  crypto.getRandomValues(randomBuffer);
  
  // Convert to number in range [min, max]
  const range = max - min + 1;
  const randomValue = randomBuffer[0] / (0xffffffff + 1); // 0 to 1
  
  return Math.floor(randomValue * range) + min;
}

// Example: Generate number between 1 and 100
const luckyNumber = generateSecureRandom(1, 100);`}
            </pre>
            <p className="text-sm text-muted-foreground mt-3">
              <strong>Note:</strong> Our production code includes additional bias-correction algorithms to ensure 
              perfect uniform distribution across all possible values.
            </p>
          </CardContent>
        </Card>

        {/* Security Properties */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-primary" />
              Security Properties
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" />
              <div>
                <strong>Unpredictability:</strong> Even with knowledge of previous outputs, future values cannot be predicted
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" />
              <div>
                <strong>Non-Reproducibility:</strong> Each generation uses fresh entropy from hardware/OS sources
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" />
              <div>
                <strong>Uniform Distribution:</strong> All values in range have equal probability (within statistical limits)
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" />
              <div>
                <strong>NIST Compliance:</strong> Implements NIST SP 800-90A Deterministic Random Bit Generators
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" />
              <div>
                <strong>No Server Required:</strong> All generation happens in your browser - your numbers never leave your device
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Limitations & When to Use True Random */}
        <Card className="mb-8 border-yellow-500/20 bg-yellow-500/5">
          <CardHeader>
            <CardTitle className="text-yellow-600 dark:text-yellow-400">
              Pseudo-Random vs True Random: What's the Difference?
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h3 className="font-semibold mb-2">RandomGen Uses: Pseudo-Random (CSPRNG)</h3>
              <p className="text-muted-foreground mb-2">
                While our generator is <em>cryptographically secure</em>, it's technically a <strong>pseudo-random</strong> generator. 
                This means it uses deterministic algorithms seeded with high-quality entropy.
              </p>
              <p className="text-muted-foreground">
                <strong>Perfect for:</strong> Lotteries, raffles, games, passwords, cryptographic keys, simulations, sampling
              </p>
            </div>

            <div>
              <h3 className="font-semibold mb-2">True Random Generators (e.g., Random.org)</h3>
              <p className="text-muted-foreground mb-2">
                True random generators measure physical phenomena (atmospheric noise, radioactive decay, quantum events) 
                to generate numbers with theoretical perfect randomness.
              </p>
              <p className="text-muted-foreground">
                <strong>Tradeoffs:</strong> Slower (network latency), requires external service, limited generation rate
              </p>
            </div>

            <div className="bg-background/50 p-4 rounded border border-border">
              <p className="font-semibold mb-2">Our Recommendation:</p>
              <p className="text-sm text-muted-foreground">
                For 99.9% of use cases (including high-security applications like cryptocurrency), 
                CSPRNGs like ours are <strong>more than sufficient</strong> and are the industry standard. 
                True random sources are mainly used in specialized scientific or paranoia-grade cryptographic research.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Browser Compatibility */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Browser Compatibility</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground mb-3">
              The Web Crypto API is supported in all modern browsers:
            </p>
            <ul className="grid grid-cols-2 gap-2 text-sm text-muted-foreground">
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-accent" />
                Chrome 11+ (2011)
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-accent" />
                Firefox 21+ (2013)
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-accent" />
                Safari 6.1+ (2013)
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-accent" />
                Edge (all versions)
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-accent" />
                Opera 15+ (2013)
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-accent" />
                Mobile browsers (iOS/Android)
              </li>
            </ul>
            <p className="text-sm text-muted-foreground mt-3">
              <strong>Coverage:</strong> &gt;98% of global web traffic
            </p>
          </CardContent>
        </Card>

        {/* References */}
        <Card>
          <CardHeader>
            <CardTitle>Technical References</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li>
                <a 
                  href="https://www.w3.org/TR/WebCryptoAPI/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  W3C Web Cryptography API Specification
                </a>
              </li>
              <li>
                <a 
                  href="https://csrc.nist.gov/publications/detail/sp/800-90a/rev-1/final" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  NIST SP 800-90A: Deterministic Random Bit Generators
                </a>
              </li>
              <li>
                <a 
                  href="https://developer.mozilla.org/en-US/docs/Web/API/Crypto/getRandomValues" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  MDN Documentation: crypto.getRandomValues()
                </a>
              </li>
              <li>
                <a 
                  href="https://en.wikipedia.org/wiki/Cryptographically_secure_pseudorandom_number_generator" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  Wikipedia: Cryptographically Secure PRNGs
                </a>
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* CTA */}
        <div className="mt-12 text-center">
          <p className="text-muted-foreground mb-4">
            Want to see statistical proof of our randomness quality?
          </p>
          <a 
            href="/randomness-verification"
            className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-lg hover:bg-primary/90 transition-colors font-semibold"
          >
            <Shield className="w-5 h-5" />
            View Statistical Randomness Verification Tests
          </a>
        </div>
      </div>
    </Layout>
  );
};

export default HowItWorks;
